import {
  CalendarTimeRangeConfig,
  IntervalJSON,
  RelativeTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import { groupBy } from '@highimpact/data-utils/array';

// units considered "day or less" -> produce RelativeTimeRangeConfig
const RELATIVE_ONLY_UNITS: RelativeTimeRangeConfig['unit'][] = [
  'millisecondes',
  'seconds',
  'minutes',
  'hours',
  'days',
  'weeks',
  'months',
  'years',
];

export function pluralUnitFrenchLabel(unit: RelativeTimeRangeConfig['unit']) {
  switch (unit) {
    case 'millisecondes':
      return 'millisecondes';
    case 'seconds':
      return 'secondes';
    case 'minutes':
      return 'minutes';
    case 'hours':
      return 'heures';
    case 'days':
      return 'jours';
    case 'weeks':
      return 'semaines';
    case 'months':
      return 'mois';
    case 'years':
      return 'années';
  }
}

export function singularUnitFrenchLabel(
  unit: RelativeTimeRangeConfig['unit']
): string {
  switch (unit) {
    case 'millisecondes':
      return 'milliseconde';
    case 'seconds':
      return 'seconde';
    case 'minutes':
      return 'minute';
    case 'hours':
      return 'heure';
    case 'days':
      return 'jour';
    case 'weeks':
      return 'semaine';
    case 'months':
      return 'mois';
    case 'years':
      return 'année';
  }
}

function buildName(
  quantity: number,
  unit: RelativeTimeRangeConfig['unit'],
  period_shift?: number | null
): string {
  const pluralLabel = pluralUnitFrenchLabel(unit);
  const singularLabel = singularUnitFrenchLabel(unit);

  const isMasculine = unit === 'days' || unit === 'months';

  const articleForLast = isMasculine ? 'le' : 'la';
  const courantAdj = isMasculine ? 'courant' : 'courante';
  const dernierAdj = isMasculine ? 'dernier' : 'dernière';
  const avantDernierAdj = isMasculine ? "l'avant-dernier" : "l'avant-dernière";

  const backBy = (n: number) => {
    const unitLabel = n > 1 ? pluralLabel : singularLabel;
    return `${n} ${unitLabel} en arrière`;
  };

  // quantity === 1: contextual names depending on period_shift
  if (quantity === 1) {
    if (period_shift === null || period_shift === undefined) {
      // fallback
      return `1 ${singularLabel}`;
    }
    if (period_shift === 0) {
      return `${singularLabel} ${courantAdj}`;
    }
    if (period_shift === 1) {
      return `${articleForLast} ${singularLabel} ${dernierAdj}`;
    }
    if (period_shift === 2) {
      return `${avantDernierAdj} ${singularLabel}`;
    }
    return backBy(period_shift);
  }

  // quantity > 1: base name with plural adjective
  const pluralAdj = isMasculine ? 'derniers' : 'dernières';
  const base = `${quantity} ${pluralAdj} ${pluralLabel}`;

  if (period_shift === null || period_shift === undefined) {
    return base;
  }

  const offset = period_shift * quantity;
  if (offset === 0) return base;
  return `${base} ${backBy(offset)}`;
}

/**
 * Type guard to ensure all intervals are either RelativeTimeRangeConfig or CalendarTimeRangeConfig (i.e. no fixed_time_range)
 */
function areIntervalsNonFixed(
  group: IntervalJSON[]
): group is (RelativeTimeRangeConfig | CalendarTimeRangeConfig)[] {
  return group.every((it) => it.type !== 'fixed_time_range');
}

/**
 * Validates a sequence of intervals for a specific direction and infers its quantity.
 * The intervals must all be relative, share the same unit, possess valid period shifts,
 * and strictly progress in the expected direction (decreasing for before, increasing for after).
 */
function validateAndInferGroup(
  group: (RelativeTimeRangeConfig | CalendarTimeRangeConfig)[] | undefined,
  isFuture: boolean
): { quantity: number; unit: RelativeTimeRangeConfig['unit'] } | undefined {
  if (!group || group.length === 0) return undefined;

  if (group.length === 1) {
    const first = group[0];
    if (!first || !first.unit || !first.quantity) return undefined;
    return { quantity: first.quantity, unit: first.unit };
  }

  const first = group[0];
  if (!first || !first.unit) return undefined;

  const targetUnit = first.unit;
  const hasValidStructure = group.every(
    (it) =>
      it.unit === targetUnit &&
      it.period_shift !== null &&
      it.period_shift !== undefined
  );
  if (!hasValidStructure) return undefined;

  const shifts = (
    group as (RelativeTimeRangeConfig | CalendarTimeRangeConfig)[]
  ).map((it) => it.period_shift as number);

  for (let i = 1; i < shifts.length; i++) {
    const previous = shifts[i - 1];
    const current = shifts[i];
    if (previous === undefined || current === undefined) return undefined;

    if (isFuture) {
      if (previous >= current) return undefined;
    } else {
      if (previous <= current) return undefined;
    }
  }

  const firstShift = shifts[0];
  const lastShift = shifts[shifts.length - 1];
  if (firstShift === undefined || lastShift === undefined) return undefined;

  const quantity = isFuture ? lastShift + 1 : firstShift + 1;

  return {
    quantity,
    unit: targetUnit,
  };
}

/**
 * Parse defaultIntervals to extract initial quantity and unit selection.
 *
 * Rules:
 * - single interval => return its quantity and unit (unit normalized to one of RELATIVE_UNITS)
 * - two intervals (one before, one after) => return quantities and units for before and after
 * - multiple intervals => ensure same unit across items, period_shift strictly decreasing, relative_to === 'now'
 *   and deduce quantity = first.period_shift + 1
 */
export function parseDefaultRelativeIntervals(intervals?: IntervalJSON[]):
  | {
      quantity: number;
      unit: RelativeTimeRangeConfig['unit'];
      quantityFuture?: number;
      unitFuture?: RelativeTimeRangeConfig['unit'];
    }
  | undefined {
  if (!intervals || intervals.length === 0) return undefined;

  if (!areIntervalsNonFixed(intervals)) return undefined;

  const allRelativeToNow = intervals.every((it) => it.relative_to === 'now');
  if (!allRelativeToNow) return undefined;

  const splitIntervals = groupBy(intervals, (tr) => tr.direction ?? 'before');

  const beforeGroup = splitIntervals['before'];
  const beforeResult = validateAndInferGroup(beforeGroup, false);
  if (!beforeResult) return undefined;

  const afterGroup = splitIntervals['after'];
  const afterResult = validateAndInferGroup(afterGroup, true);

  if (afterResult) {
    return {
      quantity: beforeResult.quantity,
      unit: beforeResult.unit,
      quantityFuture: afterResult.quantity,
      unitFuture: afterResult.unit,
    };
  }

  return {
    quantity: beforeResult.quantity,
    unit: beforeResult.unit,
  };
}

/**
 * Build intervals according to rules:
 * - relative_to always 'now'
 * - if unit is day or less => RelativeTimeRangeConfig
 * - otherwise => CalendarTimeRangeConfig
 * - noDivision => single interval with quantity as chosen (period_shift null)
 * - noDivision === false => create `quantity` intervals each with quantity:1 and period_shift decreasing from quantity-1 to 0
 */
export function buildRelativeTimeRanges(
  quantity: number,
  unit: RelativeTimeRangeConfig['unit'],
  noDivision = false,
  direction: 'before' | 'after' = 'before'
): IntervalJSON[] {
  const name = buildName(quantity, unit, 0);
  const isRelative = RELATIVE_ONLY_UNITS.includes(unit);

  if (noDivision) {
    if (isRelative) {
      const r: RelativeTimeRangeConfig = {
        type: 'relative_time_range',
        name,
        unit,
        quantity,
        period_shift: 0,
        direction,
        relative_to: 'now',
      };
      return [r];
    } else {
      const c: CalendarTimeRangeConfig = {
        type: 'calendar_time_range',
        name,
        unit,
        quantity,
        period_shift: 0,
        direction,
        relative_to: 'now',
      };
      return [c];
    }
  }

  // divided into `quantity` intervals each with quantity:1 and period_shift decreasing
  const ranges: IntervalJSON[] = [];
  for (let i = 0; i < quantity; i++) {
    const shift = direction === 'after' ? i : quantity - 1 - i;
    if (isRelative) {
      ranges.push({
        type: 'relative_time_range',
        name: buildName(1, unit, shift),
        unit,
        quantity: 1,
        period_shift: shift,
        direction,
        relative_to: 'now',
      });
    } else {
      ranges.push({
        type: 'calendar_time_range',
        name: buildName(1, unit, shift),
        unit: unit,
        quantity: 1,
        period_shift: shift,
        direction,
        relative_to: 'now',
      });
    }
  }

  return ranges;
}
